import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Search, UserPlus, Wrench, Coffee, Ban, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import api, { formatApiError } from '../api';
import { isoToParisParts, parisPartsToIso, durationMinutes } from '../tz';
import { INTERVENTION_TYPES } from '../constants';

const TYPE_TABS = [
  { key: 'intervention', label: 'Intervention', icon: Wrench },
  { key: 'pause', label: 'Pause', icon: Coffee },
  { key: 'indisponibilite', label: 'Indispo', icon: Ban },
];

const QuickEventDialog = ({ open, onClose, slot, prefill, onCreated }) => {
  const [type, setType] = useState('intervention');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState('60');
  const [title, setTitle] = useState('');
  const [metier, setMetier] = useState('depannage');

  // client
  const [mode, setMode] = useState('search'); // search | new
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [useOtherLocation, setUseOtherLocation] = useState(false);
  const [locAddress, setLocAddress] = useState('');
  const [locCity, setLocCity] = useState('');
  const [locPostal, setLocPostal] = useState('');

  const [saving, setSaving] = useState(false);
  const searchTimer = useRef(null);
  const savingRef = useRef(false);

  useEffect(() => {
    if (open && slot) {
      const p = isoToParisParts(slot.start);
      setDate(p.date); setTime(p.time);
      const d = Math.max(30, durationMinutes(slot.start, slot.end) || 60);
      setDuration(String(d < 30 ? 60 : d));
      // reset
      setTitle(prefill?.title || '');
      setMetier('depannage');
      setType(prefill?.interventionId ? 'intervention' : 'intervention');
      setMode('search'); setQuery(''); setResults([]);
      setSelectedClient(prefill?.client || null);
      setNewName(''); setNewPhone('');
      setUseOtherLocation(false); setLocAddress(''); setLocCity(''); setLocPostal('');
    }
  }, [open, slot, prefill]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (mode !== 'search' || query.length < 2) { setResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/clients?search=${encodeURIComponent(query)}`);
        setResults(data.slice(0, 6));
      } catch (e) { /* ignore */ }
    }, 300);
  }, [query, mode]);

  const buildTimes = () => {
    const startIso = parisPartsToIso(date, time);
    const endIso = new Date(new Date(startIso).getTime() + parseInt(duration, 10) * 60000).toISOString();
    return { startIso, endIso };
  };

  const submit = async () => {
    if (savingRef.current) return; // anti double-clic
    const { startIso, endIso } = buildTimes();

    const payload = { type, start: startIso, end: endIso };

    if (type === 'intervention') {
      if (!prefill?.interventionId) {
        if (mode === 'search') {
          if (!selectedClient) { toast.error('Sélectionnez un client'); return; }
          payload.clientId = selectedClient.id;
        } else {
          if (newName.trim().length < 2) { toast.error('Nom du client requis'); return; }
          payload.newClient = { name: newName.trim(), phone: newPhone.trim() || undefined };
        }
      } else {
        payload.interventionId = prefill.interventionId;
      }
      if (!title.trim()) { toast.error("L'objet de l'intervention est requis"); return; }
      payload.title = title.trim();
      payload.interventionType = metier;
      if (useOtherLocation) {
        payload.location = { address: locAddress || undefined, city: locCity || undefined, postalCode: locPostal || undefined };
      }
    } else {
      payload.title = title.trim() || (type === 'pause' ? 'Pause' : 'Indisponibilité');
    }

    savingRef.current = true;
    setSaving(true);
    try {
      const { data } = await api.post('/events', payload);
      if (data.warnings && data.warnings.length > 0) {
        toast.warning(`Enregistré — chevauchement détecté (${data.warnings.length}).`);
      } else {
        toast.success('Enregistré');
      }
      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const isIntervention = type === 'intervention';
  const planningExisting = !!prefill?.interventionId;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="quick-event-dialog">
        <DialogHeader><DialogTitle>{planningExisting ? 'Planifier la demande' : 'Nouvel élément'}</DialogTitle></DialogHeader>

        {!planningExisting && (
          <div className="grid grid-cols-3 gap-2">
            {TYPE_TABS.map((t) => (
              <button key={t.key} onClick={() => setType(t.key)} data-testid={`type-tab-${t.key}`}
                className={`flex flex-col items-center gap-1 py-2 rounded-lg border text-xs font-medium transition-colors ${
                  type === t.key ? 'border-red-700 bg-red-50 text-red-700' : 'border-neutral-200 text-neutral-500 hover:border-neutral-300'
                }`}>
                <t.icon className="w-4 h-4" />{t.label}
              </button>
            ))}
          </div>
        )}

        <div className="space-y-3 mt-2">
          {/* Créneau */}
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="event-date" />
            </div>
            <div className="col-span-1">
              <Label className="text-xs">Heure</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} step="900" data-testid="event-time" />
            </div>
            <div className="col-span-1">
              <Label className="text-xs">Durée</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger data-testid="event-duration"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 min</SelectItem>
                  <SelectItem value="60">1 h</SelectItem>
                  <SelectItem value="90">1 h 30</SelectItem>
                  <SelectItem value="120">2 h</SelectItem>
                  <SelectItem value="180">3 h</SelectItem>
                  <SelectItem value="240">4 h</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {isIntervention && !planningExisting && (
            <>
              {/* Client */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Client</Label>
                  <button onClick={() => { setMode(mode === 'search' ? 'new' : 'search'); setSelectedClient(null); }}
                    className="text-xs text-red-700 hover:underline" data-testid="toggle-client-mode">
                    {mode === 'search' ? '+ Nouveau client' : '← Rechercher'}
                  </button>
                </div>
                {mode === 'search' ? (
                  selectedClient ? (
                    <div className="flex items-center justify-between bg-neutral-50 rounded-lg px-3 py-2 border border-neutral-200" data-testid="selected-client">
                      <div>
                        <p className="text-sm font-medium">{selectedClient.name}</p>
                        <p className="text-xs text-neutral-500">{selectedClient.phone || 'Sans téléphone'} {selectedClient.city ? `· ${selectedClient.city}` : ''}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedClient(null)}>Changer</Button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                      <Input className="pl-9" placeholder="Nom ou téléphone..." value={query}
                        onChange={(e) => setQuery(e.target.value)} data-testid="client-search" />
                      {results.length > 0 && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {results.map((c) => (
                            <button key={c.id} onClick={() => { setSelectedClient(c); setResults([]); setQuery(''); }}
                              data-testid={`client-result-${c.id}`}
                              className="flex items-center justify-between w-full px-3 py-2 hover:bg-neutral-50 text-left">
                              <span className="text-sm">{c.name}</span>
                              <span className="text-xs text-neutral-400">{c.phone}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  <div className="grid grid-cols-2 gap-2 bg-neutral-50 p-3 rounded-lg border border-neutral-200">
                    <div className="col-span-2 text-xs text-neutral-500 flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" />Création express (sans compte)</div>
                    <Input placeholder="Nom / Société *" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-client-name" />
                    <Input placeholder="Téléphone" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} data-testid="new-client-phone" />
                  </div>
                )}
              </div>

              {/* Métier */}
              <div>
                <Label className="text-xs">Métier / Type</Label>
                <Select value={metier} onValueChange={setMetier}>
                  <SelectTrigger data-testid="event-metier"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(INTERVENTION_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </>
          )}

          {planningExisting && (
            <div className="bg-neutral-50 rounded-lg px-3 py-2 border border-neutral-200 text-sm" data-testid="planning-client">
              {prefill.clientName} — {prefill.number}
            </div>
          )}

          {/* Objet / Titre */}
          <div>
            <Label className="text-xs">{isIntervention ? "Objet de l'intervention *" : 'Libellé'}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder={isIntervention ? 'Ex : Remplacement vitre cassée' : (type === 'pause' ? 'Pause déjeuner' : 'Indisponibilité')}
              data-testid="event-title" />
          </div>

          {/* Lieu */}
          {isIntervention && !planningExisting && (
            <div>
              <label className="flex items-center gap-2 text-sm text-neutral-600">
                <input type="checkbox" checked={useOtherLocation} onChange={(e) => setUseOtherLocation(e.target.checked)} data-testid="other-location-toggle" />
                Lieu différent de l'adresse client
              </label>
              {useOtherLocation && (
                <div className="grid grid-cols-6 gap-2 mt-2">
                  <Input className="col-span-6" placeholder="Adresse" value={locAddress} onChange={(e) => setLocAddress(e.target.value)} data-testid="loc-address" />
                  <Input className="col-span-4" placeholder="Ville" value={locCity} onChange={(e) => setLocCity(e.target.value)} data-testid="loc-city" />
                  <Input className="col-span-2" placeholder="CP" value={locPostal} onChange={(e) => setLocPostal(e.target.value)} data-testid="loc-postal" />
                </div>
              )}
            </div>
          )}
        </div>

        <Button onClick={submit} disabled={saving} className="w-full bg-red-700 hover:bg-red-600 mt-3 h-11" data-testid="save-event-button">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}Enregistrer
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default QuickEventDialog;
